/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import Grid from '@material-ui/core/Grid';
import { InputLabel, useMediaQuery } from '@material-ui/core';
import { compose } from 'recompose';
import Typography from '@material-ui/core/Typography';
import ReactMarkdown from 'react-markdown';
import { withStyles } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import {
    object, shape, string, arrayOf, number, bool, func,
} from 'prop-types';
import SubscriptionPDP from '../../../SubscriptionInformation/SubscriptionPDP';
import SubscriptionDropDown from '../SubscriptionDropDown';

const styles = (theme) => ({
    subscriptionContainer: {
        border: `1px solid ${(theme.palette.grey8)}`,
        borderRadius: '4px',
        padding: '9px 14px',
        [theme.breakpoints.down('sm')]: {
            padding: '9px 5px',
        },
    },
    marginBottom15: {
        marginBottom: '15px',
    },
    subContainer: {
        display: 'flex',
        maxWidth: '100%',
        '& > div': {
            margin: '0px 10px',
            [theme.breakpoints.down('1330')]: {
                margin: '0px',
            },

        },
        [theme.breakpoints.down('xs')]: {
            '& > div': {
                width: '100%',
            },
        },
    },
    [theme.breakpoints.down('xs')]: {
        subContainerSm: {
            marginTop: 10,
        },
    },
    icon: {
        color: theme?.palette?.cms?.icon,
        margin: 'auto',
    },
    borderRight1: {
        [theme.breakpoints.up('sm')]: {
            borderRight: `1px solid ${(theme.palette.grey8)}`,
        },
    },
    marginTopBottom10: {
        margin: '10px 0px',
    },
    subcriptionContent: {
        '& p': {
            fontSize: 16,
            margin: 4,
            [theme.breakpoints.down('xs')]: {
                margin: 2,
            },
            [theme.breakpoints.down('sm')]: {
                fontSize: 14,
            },
        },
    },
    fontSize67: {
        fontSize: 64,
        [theme.breakpoints.down('md')]: {
            fontSize: 45,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: 35,
        },
        [theme.breakpoints.down('xs')]: {
            paddingLeft: 4,
            fontSize: 38,
        },

    },
    fontSize34: {
        fontSize: '34px',
    },
    subscriptionCheckBoxContent: {
        fontSize: 14,
        '& b': {
            fontSize: 16,
            display: 'block',
        },
    },
    checkbox: {
        color: theme.palette.cms?.checkbox,
        height: '100%',
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
            '& span svg': {
                fontSize: '24px',
            },
        },
        '&$checked': {
            color: theme.palette.cms?.checkbox,
            '&:hover': {
                backgroundColor: theme.palette.colorNeutral20,
            },
        },
        '&:hover': {
            backgroundColor: theme.palette.colorNeutral20,
        },
    },
    checked: {
        color: theme.palette.cms?.checkbox,
        '&$checked': {
            color: theme.palette.cms?.checkbox,
            '&:hover': {
                backgroundColor: theme.palette.colorNeutral20,
            },
        },
    },
    marginNone: {
        '& p': {
            margin: 0,
        },
    },
    inline: {
        display: 'inline-block',
    },
    maxWidth: {
        maxWidth: '100%',
    },
    alignImage: {
        alignItems: 'center',
    },
    paddingLeft: {
        paddingLeft: '5px',
    },
    alignSpan: {
        alignItems: 'center',
        [theme.breakpoints.up('sm')]: {
            paddingRight: '5px',
            borderRight: `1px solid ${(theme.palette.grey8)}`,
        },
        [theme.breakpoints.down('sm')]: {
            marginBottom: '15px',
            marginLeft: '2px',
        },
    },
    subscriptionDisplay: {
        display: 'none',
    },
    [theme.breakpoints.up('1225')]: {
        outerSubscriptionContainer: {
            flexWrap: 'nowrap',
        },
    },
});

const AbVariantSubscription = ({
    classes,
    brand,
    subscriptionCopy,
    subscriptionDetailsFrequencyValue,
    subscriptionDetailsDurationValue,
    subscriptionConfig,
    isShowingFrequencyDropdown,
    isSubscriptionDurationListEnabled,
    selectValue,
    selectedSku,
    handleChange,
    durationValue,
    subscriptionCheckbox,
    triggerSubscriptionCheck,
}) => {
    const isSmallDevice = useMediaQuery((theme) => theme?.breakpoints.down('sm'));

    const hasIcon = subscriptionCopy?.icon?.url || null;

    const renderIconUI = () => hasIcon && (
        <Grid item xs={2} sm={1} className={`${classes.subContainer} ${classes.alignImage}`}>
            <img className={classes.maxWidth} src={subscriptionCopy?.icon?.url} alt="subscription-icon" />
        </Grid>
    );

    const renderSingleLineCopy = () => {
        if (subscriptionCopy?.single_line_copy?.toLowerCase?.().trim?.() === 'n/a') {
            return '';
        }
        if (subscriptionCopy?.single_line_copy) {
            return <ReactMarkdown className={classes.marginNone} source={subscriptionCopy?.single_line_copy} />;
        }
        return `Start a subscription and have it delivered ${subscriptionConfig?.subscription_interval?.[subscriptionDetailsFrequencyValue] || subscriptionDetailsFrequencyValue}`;
    };

    return (
        <Grid container className={!subscriptionCheckbox ? classes.outerSubscriptionContainer : ''}>
            <Grid item sm={2} md={1} lg={1} xl={1}>
                <InputLabel
                    htmlFor="chk-monthly-subscription"
                    className={classes.subscriptionDisplay}
                >
                    Monthly Subscription
                </InputLabel>
                <Checkbox
                    id="chk-monthly-subscription"
                    data-test="pdp-check-monthly-subscription"
                    data-testid="pdp-check-monthly-subscription"
                    name="monthlySubscriptionChecked"
                    checked={subscriptionCheckbox}
                    onChange={triggerSubscriptionCheck}
                    classes={{
                        root: classes.checkbox,
                        checked: classes.checked,
                    }}
                />
                {isSmallDevice && hasIcon && (
                    <img className={classes.maxWidth} src={subscriptionCopy?.icon?.url} alt="subscription-icon" />
                )}
            </Grid>
            <Grid xs={10} sm={10} md={11} lg={11} xl={11}>
                <Grid container className={classes.outerSubscriptionContainer}>
                    <Grid item xs={12} lg={5} xl={5} md={5} sm={12} className={`${classes.subContainer} ${classes.alignSpan} ${classes.subContainerSm}`}>
                        <Typography variant="body1" className={`${classes.subscriptionCheckBoxContent}`}>
                            {subscriptionCopy?.single_line_label ? <ReactMarkdown className={classes.marginNone} source={subscriptionCopy?.single_line_label} /> : (<><strong>Make this a recurring Shipment</strong><br /></>)}
                            {renderSingleLineCopy()}
                        </Typography>
                    </Grid>
                    {!isSmallDevice && renderIconUI()}
                    <Grid item lg={hasIcon ? 6 : 5} xl={hasIcon ? 6 : 5} md={hasIcon ? 6 : 5} xs={hasIcon ? 10 : 12} sm={hasIcon ? 10 : 12} className={`${classes.subContainer} ${classes.paddingLeft}`}>
                        <div className={classes.subcriptionContent}>
                            {isShowingFrequencyDropdown ? (
                                <SubscriptionDropDown
                                    selectValue={selectValue}
                                    subscriptionsList={selectedSku.subscriptionsList}
                                    fieldLabel={subscriptionCopy?.frequency_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.frequency_label} /> : 'Frequency:'}
                                    handleChange={handleChange}
                                />
                            ) : (
                                <Typography variant="body1">
                                    {subscriptionCopy?.frequency_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.frequency_label} /> : (<strong>Frequency:</strong>)} {subscriptionConfig.subscription_interval[subscriptionDetailsFrequencyValue] || subscriptionDetailsFrequencyValue}
                                </Typography>
                            )}
                            {isSubscriptionDurationListEnabled ? (
                                <>
                                    <SubscriptionDropDown
                                        selectValue={durationValue}
                                        subscriptionsList={selectedSku.subscriptionsList}
                                        handleChange={handleChange}
                                        fieldLabel={subscriptionCopy?.duration_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.duration_label} /> : 'Duration:'}
                                        dropdownType="subscription_duration"
                                        frequencyOrDuration="duration"
                                    />
                                </>
                            ) : (
                                <Typography variant="body1">
                                    {subscriptionCopy?.duration_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.duration_label} /> : (<strong>Duration:</strong>)}  {subscriptionConfig.subscription_duration[subscriptionDetailsDurationValue] || subscriptionDetailsDurationValue}
                                </Typography>
                            )}
                        </div>
                        <Typography variant="span" className={`${classes.marginTopBottom10}`} />
                    </Grid>
                </Grid>
            </Grid>
            {subscriptionCheckbox && (
                <SubscriptionPDP
                    copy={subscriptionCopy?.offer_copy || ''}
                    brand={brand}
                />
            )}
        </Grid>
    );
};

AbVariantSubscription.propTypes = {
    classes: object.isRequired,
    subscriptionCopy: shape({
        single_line_label: string,
        frequency_label: string,
        duration_label: string,
    }),
    subscriptionDetailsFrequencyValue: number.isRequired,
    subscriptionDetailsDurationValue: number.isRequired,
    subscriptionConfig: shape({
        subscription_duration: arrayOf(number),
        subscription_interval: arrayOf(number),
    }).isRequired,
    isShowingFrequencyDropdown: bool,
    brand: shape({
        domain: string,
    }).isRequired,

    isSubscriptionDurationListEnabled: bool.isRequired,
    selectValue: string.isRequired,
    selectedSku: shape({
        subscriptionsList: shape({
            type: arrayOf(string),
        }),
    }).isRequired,
    handleChange: func.isRequired,
    durationValue: string.isRequired,
    subscriptionCheckbox: bool.isRequired,
    triggerSubscriptionCheck: func.isRequired,
};

AbVariantSubscription.defaultProps = {
    subscriptionCopy: {},
    isShowingFrequencyDropdown: false,
};

const enhance = compose(
    withStyles(styles),
);

export default enhance(AbVariantSubscription);
